(ns ui.view.transaction-process.components.permissions-modal
  (:require
   [common.entity :as entity]
   [common.localization :refer [tr]]
   [common.schema.data-types :as data-types]
   [common.transaction-process.permissions :as tp-permissions]
   [frontend.state.current-session :as session]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.util :as uu]
   [ui.view.transaction-process.util :as tp-util]))

(defn- perm-cell
  [perm]
  (let [label (case perm :w (tr :transaction-process/perm-write) :r (tr :transaction-process/perm-read) "-")
        color (case perm
                :w "text-primary-regular-text-accent font-medium"
                :r "text-gray-600"
                "text-gray-300")]
    [:span {:class (uu/cm "text-xs m-0" color)} label]))

(defn- categorize-fields
  [fields-config fields]
  (let [grouped (group-by (fn [fk]
                            (get-in fields-config [fk :field/category] :tp-fields))
                          fields)
        order [:tp-fields :decisions :verifications :declarations]
        labels {:tp-fields      (tr :transaction-process/tp-fields-category)
                :decisions      (tr :transaction-process/decision-fields-category)
                :verifications  (tr :transaction-process/verification-fields-category)
                :declarations   (tr :transaction-process/declaration-fields-category)}]
    (->> order
         (keep (fn [cat]
                 (when-let [flds (seq (get grouped cat))]
                   {:category-id    cat
                    :category-label (or (get labels cat)
                                        [tp-util/render-error
                                         {:context "Unknown field category"
                                          :value cat}])
                    :fields         (vec flds)}))))))

(defn- permissions-table
  [{:keys [sections stages fields fields-config get-perm]}]
  (let [field-groups (categorize-fields fields-config fields)
        n-cols (inc (count stages))]
    [:<>
     (doall
      (for [{:keys [section-key section-label]} sections]
        ^{:key section-key}
        [:div {:class "mb-6 border border-gray-200 rounded-lg"}
         [:table {:style {:width "100%" :border-collapse "separate" :border-spacing 0
                          :margin 0 :padding 0}}
          [:thead {:style {:margin 0 :padding 0}}
           [:tr {:style {:margin 0 :padding 0}}
            [:th {:col-span n-cols
                  :style    {:margin 0 :padding "8px 16px" :text-align "left"
                             :background "#f9fafb" :border-bottom "1px solid #e5e7eb"}}
             [:span {:style {:margin 0 :padding 0 :font-size "0.875rem" :line-height "1.25rem"
                             :font-weight 600 :color "#111827"}}
              section-label]]]
           [:tr {:style {:margin 0 :padding 0}}
            [:th {:style {:margin 0 :padding "8px 16px" :text-align "left"
                          :font-size "0.75rem" :line-height "1rem" :font-weight 500 :color "#6b7280"
                          :background "#f9fafb" :border-bottom "1px solid #e5e7eb"}}
             (tr :field)]
            (doall
             (for [stage stages]
               ^{:key (:stage/id stage)}
               [:th {:style {:margin 0 :padding "8px 12px" :text-align "center"
                             :font-size "0.75rem" :line-height "1rem" :font-weight 500 :color "#6b7280"
                             :background "#f9fafb" :border-bottom "1px solid #e5e7eb"
                             :border-left "1px solid #e5e7eb"}}
                (:stage/label stage)]))]]
          [:tbody
           (doall
            (for [{:keys [category-id category-label fields]} field-groups]
              ^{:key category-id}
              [:<>
               [:tr {:class "bg-gray-50/50"}
                [:td {:class  "text-xs m-0 font-semibold text-gray-500 uppercase tracking-wider py-1.5 px-4"
                      :col-span n-cols}
                 category-label]]
               (doall
                (for [field-kw fields]
                  (let [fc (get fields-config field-kw)
                        field-label (or (tp-util/field-label field-kw fc)
                                        [tp-util/render-error
                                         {:context (str "Missing :field/label for " field-kw)
                                          :value field-kw}])
                        data-type (:field/data-type fc)]
                    ^{:key field-kw}
                    [:tr {:class "border-b border-gray-100 last:border-b-0"}
                     [:td {:class "text-sm m-0 text-gray-700 py-2 px-4"}
                      field-label
                      (when data-type
                        (let [dt-label (if (tp-util/attachment-slot-field? fc)
                                         (tr :attachment)
                                         (get-in data-types/zapflow-data-types [data-type :translations :en]))]
                          [:span {:class "ml-2 text-xs m-0 text-gray-400"}
                           (or dt-label
                               [tp-util/render-error
                                {:context "Missing data-type translation"
                                 :value data-type}])]))]
                     (doall
                      (for [stage stages]
                        ^{:key (:stage/id stage)}
                        [:td {:class "text-center py-2 px-3 border-l border-gray-100"}
                         [perm-cell (get-perm section-key field-kw (:stage/id stage))]]))])))]))]]]))]))

(defn- role-permissions-table
  [{:keys [template]}]
  (let [team-roles (:template/team-roles template)
        stages (:template/stages template)
        fields (keys (:template/permissions template))
        fields-config (:template/fields template)]
    [permissions-table
     {:sections      (mapv (fn [r] {:section-key (:team-role/id r)
                                    :section-label (:team-role/label r)})
                           team-roles)
      :stages        stages
      :fields        fields
      :fields-config fields-config
      :get-perm      (fn [role-id field-kw stage-id]
                       (get-in (:template/permissions template)
                               [field-kw stage-id role-id] :none))}]))

(defn- account-permissions-table
  [{:keys [template team-assignments]}]
  (let [stages (:template/stages template)
        fields (keys (:template/permissions template))
        fields-config (:template/fields template)
        all-account-ids (->> (vals team-assignments)
                             (mapcat (fn [members]
                                       (map (fn [m] (or (:value m) m)) members)))
                             (distinct))
        read-only? (session/is-read-only-user?)]
    (if (empty? all-account-ids)
      [:div {:class "text-sm m-0 text-gray-400 italic py-4 text-center"}
       (tr :transaction-process/assign-team-to-see-permissions)]
      (let [sections (->> all-account-ids
                          (mapv (fn [aid]
                                  (let [account (session/get-entity aid)]
                                    {:section-key   aid
                                     :section-label (if account
                                                      (entity/display-string account)
                                                      (tr :transaction-process/unknown-member))}))))]
        [permissions-table
         {:sections      sections
          :stages        stages
          :fields        fields
          :fields-config fields-config
          :get-perm      (fn [account-id field-kw stage-id]
                           (tp-permissions/resolve-account-field-permission
                            {:template         template
                             :team-assignments  team-assignments
                             :account-id        account-id
                             :field-kw          field-kw
                             :stage-id          stage-id
                             :read-only-user?   read-only?}))}]))))

(defn view
  [_props]
  (let [active-tab (r/atom :by-role)]
    (fn [{:keys [on-close template team-assignments]}]
      [modal/view
       {:visible?      true
        :hide!         on-close
        :size          "900px"
        :content-class "max-h-[80vh]"}
       [:div {:class "flex items-center justify-between w-full"}
        [:span (tr :permissions)]
        [:div {:class "flex gap-4"}
         [:button
          {:class (uu/cm "text-sm m-0 pb-1"
                         (if (= @active-tab :by-role)
                           "text-primary-regular-text-accent font-semibold border-b-2 border-primary-regular"
                           "text-gray-500 hover:text-gray-700"))
           :on-click #(reset! active-tab :by-role)}
          (tr :by-team-role)]
         [:button
          {:class (uu/cm "text-sm m-0 pb-1"
                         (if (= @active-tab :by-user)
                           "text-primary-regular-text-accent font-semibold border-b-2 border-primary-regular"
                           "text-gray-500 hover:text-gray-700"))
           :on-click #(reset! active-tab :by-user)}
          (tr :by-user)]]]
       (case @active-tab
         :by-role [role-permissions-table {:template template}]
         :by-user [account-permissions-table {:template         template
                                              :team-assignments team-assignments}])
       [modal/footer-children-left-and-right
        []
        [[button/secondary-hollow
          {:on-click on-close}
          (tr :close)]]]])))
